import React, { createContext, useContext, useRef, useEffect, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { callGetMyJobs } from '../api/core/api/callGetMyJobs';
import type { MyJobItem } from '../api/core/api/callGetMyJobs';

const MISSED_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

const JOB_TYPE_LABELS: Record<string, string> = {
  sync_contacts: 'Contact sync',
  artifact_performance_report: 'Performance report',
  artifact_cohort_report: 'Cohort report',
  artifact_mortgage_report: 'Mortgage report',
  artifact_negotiator_report: 'Negotiator report',
  delete_company: 'Delete company',
  signout_all_negotiator: 'Sign out all negotiator users'
};

function showJobToast(job: { jobType: string; status: string; result?: any; errorMessage?: string }) {
  const label = JOB_TYPE_LABELS[job.jobType] || job.jobType;

  if (job.status === 'completed') {
    const shareUrl = job.result?.shareUrl;
    if (shareUrl) {
      toast.success(
        <span>
          {label} ready.{' '}
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'inherit', textDecoration: 'underline' }}
          >
            Open report
          </a>
        </span>,
        { autoClose: 10000 }
      );
    } else {
      toast.success(`${label} completed successfully`);
    }
  } else if (job.status === 'failed') {
    toast.error(`${label} failed: ${job.errorMessage || 'Unknown error'}`);
  }
}

type RecentJobsContextValue = { jobs: MyJobItem[]; refetch: () => Promise<void> };

const RecentJobsContext = createContext<RecentJobsContextValue>({
  jobs: [],
  refetch: async () => {
    return;
  }
});

export function RecentJobsProvider({ children }: { children: React.ReactNode }) {
  const [jobs, setJobs] = useState<MyJobItem[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);
  const notifiedJobsRef = useRef<Set<string>>(new Set());

  const fetchJobs = useCallback(async (): Promise<void> => {
    const res = await callGetMyJobs(100);
    if (res.code !== 200 || !Array.isArray(res.data)) return;
    setJobs(res.data as MyJobItem[]);
  }, []);

  const handleJobUpdate = useCallback((event: MessageEvent) => {
    try {
      const data = JSON.parse(event.data);
      const isTerminal = data.status === 'completed' || data.status === 'failed';
      // Intermediate step events (status='running' + currentStep change) must
      // always trigger a refetch so the notification-center stepper advances in
      // real time. Only terminal events get the dedup+toast treatment.
      if (isTerminal) {
        if (notifiedJobsRef.current.has(data.jobId)) return;
        notifiedJobsRef.current.add(data.jobId);
        showJobToast(data);
      }
      fetchJobs();
    } catch {
      // Ignore malformed events
    }
  }, [fetchJobs]);

  useEffect(() => {
    // Load initial jobs and show toasts for recently completed ones
    callGetMyJobs(100).then((res) => {
      if (res.code !== 200 || !Array.isArray(res.data)) return;
      const list = res.data as MyJobItem[];
      setJobs(list);
      const now = Date.now();
      for (const job of list) {
        if (job.status !== 'completed' && job.status !== 'failed') continue;
        if (!job.completedAt) continue;
        const completedMs = new Date(job.completedAt).getTime();
        if (now - completedMs > MISSED_WINDOW_MS) continue;
        if (notifiedJobsRef.current.has(job.jobId)) continue;
        notifiedJobsRef.current.add(job.jobId);
        showJobToast(job);
      }
    });

    // Connect SSE
    const token = localStorage.getItem('token');
    if (!token) return;

    const apiBase = process.env.REACT_APP_API_BASE;
    const url = `${apiBase}/jobs/stream?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);

    es.addEventListener('job_update', handleJobUpdate);

    es.onerror = () => {
      // EventSource auto-reconnects on error; no action needed
    };

    eventSourceRef.current = es;

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [fetchJobs, handleJobUpdate]);

  const value: RecentJobsContextValue = { jobs, refetch: fetchJobs };
  return (
    <RecentJobsContext.Provider value={value}>{children}</RecentJobsContext.Provider>
  );
}

export function useRecentJobsContext() {
  return useContext(RecentJobsContext);
}
